import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Newspaper, RefreshCw, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const SOURCES = [
  { id: "toi", name: "Times of India" },
  { id: "ndtv", name: "NDTV" },
  { id: "hindu", name: "The Hindu" },
  { id: "ht", name: "Hindustan Times" },
  { id: "indiatoday", name: "India Today" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NewsCard({ card }) {
  const [source, setSource] = useState("toi");

  const { data: news = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["news", source],
    queryFn: () => api.get("/news", { params: { source } }).then((r) => r.data),
    refetchInterval: 600000,
    staleTime: 300000,
    retry: 1,
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <select data-testid="news-source-select" value={source} onChange={(e) => setSource(e.target.value)}
          className="min-w-0 flex-1 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1.5 text-xs outline-none cursor-pointer">
          {SOURCES.map((s) => <option key={s.id} value={s.id}>{`🇮🇳 ${s.name}`}</option>)}
        </select>
        <button data-testid="news-refresh-button" onClick={() => refetch()} title="Refresh"
          className="rounded-lg bg-black/10 dark:bg-white/10 p-1.5 opacity-60 transition-opacity hover:opacity-100">
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="space-y-1.5 overflow-y-auto" data-testid="news-list"
        style={{ height: card?.h ? Math.max(160, card.h - 110) : 280 }}>
        {isLoading && <div className="pt-20 text-center"><div className="nebula-loader mx-auto" /></div>}
        {isError && <p className="pt-16 text-center font-hand text-xl opacity-50">feed unavailable — try another source ✦</p>}
        {news.map((n, i) => (
          <a key={i} href={n.link} target="_blank" rel="noopener noreferrer"
            data-testid={`news-item-${i}`}
            className="group flex gap-2 rounded-xl bg-white/40 dark:bg-black/25 p-2 transition-colors hover:bg-white/70 dark:hover:bg-black/40">
            {n.image && <img src={n.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" loading="lazy" />}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold leading-snug line-clamp-2 group-hover:underline">{n.title}</p>
              <div className="mt-0.5 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide opacity-50">
                <Newspaper className="h-2.5 w-2.5" /> {n.source} {n.published && `· ${timeAgo(n.published)}`}
                <ExternalLink className="ml-auto h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
